<template>
    <DxPopup :visible="visible" :show-title="false" :drag-enabled="true" :show-close-button="false"
        :close-on-outside-click="false" :width="1000" :height="620"
        :wrapper-attr="{ class: 'import-linii-excel-popup' }" @hidden="close">
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
            :options="{ text: isAdaugand ? 'Se adaugă...' : `Adaugă ${liniiGasite.length} linie(i)`, type: 'default', disabled: isAdaugand || liniiGasite.length === 0, onClick: onAdaugaToate }" />
        <DxToolbarItem widget="dxButton" toolbar="bottom" location="after"
            :options="{ text: 'Închide', stylingMode: 'outlined', onClick: close }" />
        <template #content>
            <div class="rapida-popup">
                <div class="ehp-header">
                    <div class="ehp-header-main">
                        <i class="dx-icon dx-icon-xlsxfile ehp-icon" />
                        <div class="ehp-texts">
                            <span class="ehp-title">Import linii din Excel</span>
                            <span class="ehp-subtitle">Lipiți coduri — câte unul pe linie</span>
                        </div>
                    </div>
                    <span class="ehp-close" @click="close">×</span>
                </div>

                <div class="rapida-coduri-section">
                    <textarea ref="textareaRef" class="rapida-textarea" v-model="coduri"
                        placeholder="Lipiți coduri cu Ctrl+V — câte unul pe linie..." rows="4" />
                    <div v-if="isProcessing" class="rapida-status">Se procesează...</div>
                </div>

                <div class="rapida-results-section">
                    <template v-if="linii.length > 0">
                        <div class="rapida-results-caption">
                            Rezultate
                            <span v-if="liniiGasite.length > 0" class="rapida-badge-ok">{{ liniiGasite.length }} găsite</span>
                            <span v-if="liniiNegasite.length > 0" class="rapida-badge-err">{{ liniiNegasite.length }} negăsite</span>
                        </div>
                        <div class="rapida-table-scroll">
                            <table class="rapida-table">
                                <thead>
                                    <tr>
                                        <th style="width:28px"></th>
                                        <th style="width:110px">Cod</th>
                                        <th style="min-width:160px">Denumire</th>
                                        <th style="width:100px">Cantitate</th>
                                        <th style="width:120px">Preț Unitar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(linie, i) in linii" :key="i"
                                        :class="linie.gasit ? 'rapida-row-ok' : 'rapida-row-err'">
                                        <td class="rapida-status-cell">
                                            <i class="dx-icon" :class="linie.gasit ? 'dx-icon-check' : 'dx-icon-close'" />
                                        </td>
                                        <td class="rapida-cod-cell">{{ linie.cod }}</td>
                                        <td class="rapida-den-cell">
                                            <span v-if="linie.gasit">{{ linie.produs.Nume }}</span>
                                            <span v-else class="rapida-negasit">Nu a fost găsit</span>
                                        </td>
                                        <td class="rapida-input-cell">
                                            <DxNumberBox v-if="linie.gasit" v-model:value="linie.cantitate"
                                                :min="0.0001" :format="formatRO" width="100%" />
                                        </td>
                                        <td class="rapida-input-cell">
                                            <DxNumberBox v-if="linie.gasit" v-model:value="linie.pretUnitar"
                                                :min="0" :format="formatRO" width="100%" />
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>
                    <div v-else class="rapida-empty-state">
                        <i class="dx-icon dx-icon-paste" style="font-size:32px; color:#cbd5e1; margin-bottom:10px" />
                        <span>Lipiți coduri în câmpul de mai sus (Ctrl+V)</span>
                    </div>
                </div>
            </div>
        </template>
    </DxPopup>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import DxPopup, { DxToolbarItem } from 'devextreme-vue/popup';
import DxNumberBox from 'devextreme-vue/number-box';
import notify from 'devextreme/ui/notify';
import http from '@/settings/apiSettings';
import DOC_Linii from '@/services/Documente/DOC_Linii';

interface DocumentProp {
    Id?: string;
    [key: string]: unknown;
}

interface ImportLinie {
    cod: string;
    produs: any | null;
    gasit: boolean;
    cantitate: number;
    pretUnitar: number | null;
}

const props = defineProps<{ document: DocumentProp; visible: boolean }>();
const emit = defineEmits<{
    (e: 'update:visible', value: boolean): void;
    (e: 'linii-added'): void;
}>();

const coduri = ref('');
const linii = ref<ImportLinie[]>([]);
const isProcessing = ref(false);
const isAdaugand = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const liniiGasite = computed(() => linii.value.filter((l) => l.gasit));
const liniiNegasite = computed(() => linii.value.filter((l) => !l.gasit));

function formatRO(val: number | null | undefined): string {
    if (val == null) return '';
    return val.toLocaleString('ro-RO', { maximumFractionDigits: 10 });
}

function getErrMsg(err: any): string {
    const data = err?.response?.data;
    if (data) {
        if (typeof data === 'string') return data;
        if (data.error?.message) return data.error.message;
        if (data.message) return data.message;
        if (data.title) return data.title;
    }
    return err?.message ?? 'A apărut o eroare.';
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(coduri, (val) => {
    if (!val.trim()) { linii.value = []; return; }
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => proceseazaCoduri(), 500);
});

watch(() => props.visible, (val) => {
    if (val) {
        coduri.value = '';
        linii.value = [];
        nextTick(() => textareaRef.value?.focus());
    }
});

async function proceseazaCoduri() {
    const listaCoduri = coduri.value.split('\n').map((c) => c.trim()).filter((c) => c.length > 0);
    if (listaCoduri.length === 0) { linii.value = []; return; }
    isProcessing.value = true;
    try {
        // Deduplicare păstrând ordinea/case-ul original — la fel ca la Comenzi.
        const seenLower = new Set<string>();
        const unice: string[] = [];
        for (const cod of listaCoduri) {
            if (!seenLower.has(cod.toLowerCase())) {
                seenLower.add(cod.toLowerCase());
                unice.push(cod);
            }
        }

        // Interogare în loturi mici — fiecare cod adaugă ~5-6 noduri la $filter (2x "eq" + "or" +
        // conectorul dintre grupuri), iar OData respinge peste 100 de noduri per query (implicit
        // ASP.NET OData). Loturi de 10 coduri rămân sigur sub prag indiferent câte coduri se lipesc.
        const LOT_SIZE = 10;
        const loturi: string[][] = [];
        for (let i = 0; i < unice.length; i += LOT_SIZE) {
            loturi.push(unice.slice(i, i + LOT_SIZE));
        }

        const raspunsuri = await Promise.all(
            loturi.map((lot) => {
                const filterStr = lot
                    .map((c) => `(Cod eq '${c.replace(/'/g, "''")}' or CodIntern eq '${c.replace(/'/g, "''")}')`)
                    .join(' or ');
                return http.clientDocumenteOdata.get('PRO_Produse', {
                    params: {
                        '$filter': filterStr,
                        '$select': 'Id,Cod,CodIntern,Nume,UnitateId,CategorieContabilaId',
                    },
                });
            })
        );
        const produse: any[] = raspunsuri.flatMap((resp) => resp.data?.value ?? []);

        linii.value = listaCoduri.map((cod) => {
            const produs = produse.find((p) =>
                p.Cod?.toLowerCase() === cod.toLowerCase() ||
                p.CodIntern?.toLowerCase() === cod.toLowerCase()
            ) ?? null;
            return {
                cod,
                produs,
                gasit: !!produs,
                cantitate: 1,
                pretUnitar: null,
            };
        });
    } catch (err: any) {
        notify(getErrMsg(err), 'error', 3000);
        linii.value = listaCoduri.map((cod) => ({ cod, produs: null, gasit: false, cantitate: 1, pretUnitar: null }));
    } finally {
        isProcessing.value = false;
    }
}

function close() {
    emit('update:visible', false);
}

async function onAdaugaToate() {
    const gasite = liniiGasite.value;
    if (gasite.length === 0 || !props.document?.Id) return;
    isAdaugand.value = true;
    let adaugate = 0;
    try {
        for (const linie of gasite) {
            await DOC_Linii.save(props.document.Id, {
                ProdusId: linie.produs.Id,
                ProdusNume: linie.produs.Nume ?? null,
                UnitateId: linie.produs.UnitateId ?? null,
                CategorieContabilaId: linie.produs.CategorieContabilaId ?? null,
                Cantitate: linie.cantitate ?? 1,
                PretUnitar: linie.pretUnitar ?? null,
            });
            adaugate++;
        }
        notify(`${adaugate} linie(i) adăugate cu succes.`, 'success', 2000);
        emit('linii-added');
        coduri.value = '';
        linii.value = [];
        close();
    } catch (err: any) {
        if (adaugate > 0) {
            emit('linii-added');
            notify(`${adaugate} din ${gasite.length} linii adăugate. ${getErrMsg(err)}`, 'warning', 5000);
        } else {
            notify(getErrMsg(err), 'error', 3000);
        }
    } finally {
        isAdaugand.value = false;
    }
}
</script>

<style scoped>
.import-linii-excel-popup :deep(.dx-popup-content) {
    padding: 0 !important;
    overflow: hidden;
    height: 100% !important;
}

.rapida-popup {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.ehp-header {
    flex-shrink: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    color: #fff;
}

.ehp-header-main {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ehp-icon {
    font-size: 20px;
}

.ehp-texts {
    display: flex;
    flex-direction: column;
}

.ehp-title {
    font-size: 15px;
    font-weight: 700;
}

.ehp-subtitle {
    font-size: 12px;
    opacity: 0.85;
}

.ehp-close {
    font-size: 22px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    line-height: 1;
    padding: 0 2px;
    transition: color 0.15s;
}

.ehp-close:hover {
    color: #fff;
}

.rapida-coduri-section {
    flex-shrink: 0;
    background: white;
}

.rapida-results-section {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.rapida-results-caption {
    flex-shrink: 0;
    background: #f8f9fa;
    color: #333;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 12px;
    border-left: 3px solid #ff2c21;
    border-bottom: 1px solid #e9ecef;
}

.rapida-table-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: auto;
}

.rapida-empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #94a3b8;
    background: #f8f9fc;
    gap: 4px;
}

.rapida-status {
    padding: 4px 12px 6px;
    font-size: 12px;
    color: #94a3b8;
}

.rapida-textarea {
    width: 100%;
    box-sizing: border-box;
    font-size: 13px;
    font-family: 'Roboto', monospace;
    border: none;
    border-bottom: 1px solid #e9ecef;
    border-radius: 0;
    padding: 10px 14px;
    resize: none;
    outline: none;
    line-height: 1.5;
    color: #334155;
    display: block;
}

.rapida-textarea:focus {
    border-bottom-color: #ff2c21;
    box-shadow: none;
}

.rapida-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.rapida-table thead th {
    position: sticky;
    top: 0;
    background: #f8f9fa;
    font-weight: 600;
    font-size: 11px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
}

.rapida-table tbody tr {
    border-bottom: 1px solid #f1f5f9;
}

.rapida-table tbody td {
    padding: 5px 8px;
    vertical-align: middle;
}

.rapida-row-ok {
    background: #f0fdf4;
}

.rapida-row-err {
    background: #fff5f5;
}

.rapida-status-cell {
    text-align: center;
    width: 28px;
}

.rapida-row-ok .dx-icon-check {
    color: #16a34a;
    font-size: 16px;
}

.rapida-row-err .dx-icon-close {
    color: #dc2626;
    font-size: 16px;
}

.rapida-cod-cell {
    font-weight: 600;
    color: #334155;
}

.rapida-den-cell {
    color: #334155;
}

.rapida-negasit {
    color: #dc2626;
    font-style: italic;
    font-size: 12px;
}

.rapida-input-cell {
    padding: 3px 6px;
}

.rapida-badge-ok {
    display: inline-block;
    margin-left: 8px;
    background: #dcfce7;
    color: #166534;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
}

.rapida-badge-err {
    display: inline-block;
    margin-left: 6px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
}
</style>
